import type { UIMessage } from '@ai-sdk/react';
import type { UIDataTypes, UIMessagePart, UITools } from 'ai';

export type ChatMessagePart = UIMessagePart<UIDataTypes, UITools>;

export const isToolPart = (part: ChatMessagePart) => typeof part.type === 'string' && part.type.startsWith('tool-');

export const getToolState = (part: ChatMessagePart) => ('state' in part ? part.state : undefined);

export const isActiveToolPart = (part: ChatMessagePart) => {
  if (!isToolPart(part)) return false;

  const state = getToolState(part);
  return state !== 'output-available' && state !== 'output-error';
};

export const shouldShowAssistantLoadingFallback = (parts: ChatMessagePart[], { hideTools = false }: { hideTools?: boolean } = {}) => {
  const hasActiveTool = parts.some(isActiveToolPart);

  if (hasActiveTool) return hideTools;
  return !hasVisibleAssistantTextPart(parts);
};

export const getToolLoadingKey = (part: ChatMessagePart, index: number) => {
  if ('toolCallId' in part && typeof part.toolCallId === 'string') return part.toolCallId;
  return `${part.type}-${index}`;
};

export const getMessageText = (message: UIMessage) =>
  message.parts
    .filter((part): part is Extract<ChatMessagePart, { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('\n');

const normalizePreambleText = (value: string) =>
  value
    .trim()
    .replace(/^[>\s#*_`~\-•\d.)]+/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const assistantProgressPreamblePatterns = [
  /^(ja\s+(estou|vou)|estou|vou)\s+(buscar|buscando|consultar|consultando|coletar|coletando|carregar|carregando|usar|usando|analisar|analisando|verificar|verificando)\b/,
  /^entendido[!.:]?\s+(vou|irei|estou)\s+(analisar|analisando|buscar|buscando|consultar|consultando|coletar|coletando|carregar|carregando)\b/,
  /^preciso\s+(dos?|das?|de|buscar|consultar|acessar|carregar)\b/,
  /^os dados retornados\s+sao\b/,
  /^agora\s+(vou|estou|carregando|buscando|consultando|usando|usarei)\b/,
  /^agora vou\b/,
  /^ja\s+tenho\s+(os\s+)?dados[!.:,;]?\s+agora\s+(vou|estou)\s+(processar|processando|calcular|calculando|analisar|analisando)\b/,
  /^(perfeito|otimo|certo|ok|beleza|excelente)[!.:]?\s+(ja\s+)?tenho\s+(os\s+)?dados\b.*\bagora\s+(vou|estou)\b/,
  /^(otimo|perfeito|certo|ok|beleza)[!.:]?\s*agora\b/,
  /^(excelente)[!.:]?\s*agora\b/,
  /^(deixa|deixe)\s+(eu\s+)?(buscar|consultar|carregar|usar|verificar)\b/,
  /^(buscando|consultando|carregando|coletando)\b.*\b(dados|informacoes|ferramentas)\b/,
  /^um instante\b/,
];

const isAssistantProgressPreamble = (value: string) => {
  const normalizedValue = normalizePreambleText(value);

  return assistantProgressPreamblePatterns.some((pattern) => pattern.test(normalizedValue));
};

export const stripAssistantProgressPreamble = (value: string) => {
  const blocks = value.split(/\n{2,}/);
  const firstFinalBlockIndex = blocks.findIndex((block) => !isAssistantProgressPreamble(block));

  if (firstFinalBlockIndex === -1) return '';
  if (firstFinalBlockIndex === 0) return value;

  return blocks.slice(firstFinalBlockIndex).join('\n\n').trimStart();
};

export const hasVisibleAssistantTextPart = (parts: ChatMessagePart[]) => parts.some((part) => part.type === 'text' && stripAssistantProgressPreamble(part.text).trim().length > 0);
